import { useEffect, useRef, useState, CSSProperties } from "react";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { Column, ColumnEditorOptions } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast, ToastMessage } from "primereact/toast";
import UserAddressService from "../../../services/useraddress/useraddress.service";

interface DataTableProps1 {
  data: any[];
  NoofRecords: number;
  pageNumber: number;
  pageSize: number;
  onPageChange: any;
  searchTerm: any;
  fetchData: (pageNumber: number, pageSize: number) => void;
}

const CountryDatatable: React.FC<DataTableProps1> = (props) => {
  const toast = useRef<Toast>(null);
  const [data, setData] = useState<any[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<any>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    setData(props.data);
  }, [props.data]);

  const showMessage = (
    label: string,
    message: string,
    severity: ToastMessage["severity"]
  ) => {
    toast.current?.show({
      severity,
      summary: label,
      detail: message,
      life: 3000,
    });
  };

  const nameEditor = (options: ColumnEditorOptions) => (
    <InputText
      value={options.value}
      onChange={(e) => options.editorCallback!(e.target.value)}
      style={{ width: "100%", border: "1px solid green" }}
    />
  );

  const codeEditor = (options: ColumnEditorOptions) => (
    <InputText
      value={options.value}
      onChange={(e) => options.editorCallback!(e.target.value)}
      style={{ width: "100%", border: "1px solid green" }}
    />
  );

  const onRowEditComplete = async (event: DataTableRowEditCompleteEvent) => {
    const { newData, index } = event;
    const _data = [...data];

  if (!newData.countryName || newData.countryName.trim() === '') {
    showMessage("Message", "Country Name is required", "warn");
    return; 
  }
  if (!newData.countryCode || newData.countryCode.trim() === '') {
    showMessage("Message", "Country Code is required", "warn");
    return;
  }

    if (_data[index] !== newData) {
      let putData = {
        countryId: newData.countryId,
        countryName: newData.countryName,
        countryCode: newData.countryCode,
      };
      try {
        await UserAddressService.UpdateCountries(putData);
        _data[index] = newData;
        setData(_data);
        showMessage("Success", "Country updated", "success");
        props.fetchData(props.pageNumber, props.pageSize);
      } catch (err) {
        showMessage("Error", "Failed to update", "error");
      }
    }
  };

  const deleteTemplate = (rowData: any) => (
    <Button
      icon="pi pi-trash"
      onClick={() => confirmDelete(rowData)}
      className="p-button-rounded p-button-text"
      style={{ color: 'gray' }}
    />
  );

  const confirmDelete = (rowData: any) => {
    setSelectedCountry(rowData);
    setShowDeleteDialog(true);
  };

  const onHideDeleteDialog = () => {
    setShowDeleteDialog(false);
    setSelectedCountry(null);
  };

  const deleteCountry = async () => {
    try {
      await UserAddressService.deleteCountries(selectedCountry.countryId);
      showMessage("Success", "Deleted successfully", "success");
      // Refresh data after delete
      props.fetchData(props.pageNumber, props.pageSize);
    } catch (err) {
      showMessage("Error", "Delete failed", "error");
    } finally {
      onHideDeleteDialog();
    }
  };

  return (
    <div className="col-12">
      <Toast ref={toast} />
      <div className="card border-noround p-2">
        <DataTable
          value={data}
          paginator
          editMode="row"
          rows={props.pageSize}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          totalRecords={props.NoofRecords}
          lazy
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate={'{first} to {last} of {totalRecords}'}
          first={(props.pageNumber - 1) * props.pageSize}
          onPage={props.onPageChange}
          onRowEditComplete={onRowEditComplete}
        >
          <Column field="countryName" header="CountryName" editor={nameEditor} style={{ width: "32%" }} />
          <Column field="countryCode" header="CountryCode" editor={codeEditor} style={{ width: "52%" }} />
          <Column rowEditor header="Action" headerStyle={{ textAlign: "right" }} />
          <Column body={deleteTemplate} style={{ textAlign: "center", width: "6em" }} />
        </DataTable>
      </div>

      <Dialog
        visible={showDeleteDialog}
        header="Confirm Delete"
        modal
        onHide={onHideDeleteDialog}
        footer={
          <div>
            <Button label="Yes" icon="pi pi-check" className="p-button-danger" onClick={deleteCountry} />
          </div>
        }
      >
        <div className="confirmation-content">
          {selectedCountry && (
            <span>
              Are you sure you want to delete <strong>{selectedCountry.countryName}</strong>?
            </span>
          )}
        </div>
      </Dialog>
    </div>
  );
};

export default CountryDatatable;